import useActivityView from '@/data/hook/useActivityView';
import { updateDndOrdenationColumnProgress } from '@/data/services/activityElement/dndOrdenationActivityProgressServices';
import { ActivityMode } from '@/models/_deprecated/ActivityProgress';
import {
  ColumnDND,
  ColumnProgress,
  ItemProgress,
} from '@/models/DNDOrdenation';
import alert from '@/utils/shared/alert';
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { useAnswerDebounce } from '../../ActivitiesPreview.hooks';
import DNDOrdenationItemProgress from './DNDOrdenationItemProgress';

interface DNDOrdenationColumnProgressProps {
  dndOrdenationColumnProgress: ColumnProgress;
  activityMode: ActivityMode;
  setHasChange(hasChange: boolean): Promise<void> | void;
  answerColumn?: ColumnDND;
  columnProgress?: ColumnProgress;
}

type UpdateAnswerArgs = {
  items: ItemProgress[];
  itemsSource: ItemProgress[];
};

export default function DNDOrdenationColumnProgress({
  dndOrdenationColumnProgress,
  activityMode,
  setHasChange,
  answerColumn,
  columnProgress,
}: DNDOrdenationColumnProgressProps) {
  const { blockStepNavigate } = useActivityView();
  const [itemsState, setItemsState] = useState<ItemProgress[]>(
    dndOrdenationColumnProgress.itemsProgress,
  );

  const { t } = useTranslation('translation', {
    keyPrefix: 'lesson.activity.dragAndDropOrdenation',
  });

  const isDisabled = activityMode !== 'activity';

  const { debounce } = useAnswerDebounce();

  const updateAnswer = async ({ items }: UpdateAnswerArgs) => {
    await updateDndOrdenationColumnProgress(
      dndOrdenationColumnProgress.id,
      items,
    );
    await setHasChange(true);
  };

  const { mutate: update } = useMutation(updateAnswer, {
    onMutate() {
      blockStepNavigate(true);
    },
    onError(_, variables) {
      setItemsState(variables.itemsSource);
      alert.error(t('errorReordenation'));
    },
    onSettled() {
      blockStepNavigate(false);
    },
  });

  async function handleOnDragEnd(result: DropResult) {
    if (!result.destination) return;

    if (itemsState) {
      const itemsSource = [...itemsState];
      const items = [...itemsState];
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setItemsState(items);
      debounce(() => update({ items, itemsSource }));
    }
  }

  useEffect(() => {
    setItemsState(dndOrdenationColumnProgress.itemsProgress);
  }, [dndOrdenationColumnProgress.itemsProgress]);

  return (
    <div className="flex flex-col gap-3 items-center w-full">
      <ConditionalRenderer
        condition={!!dndOrdenationColumnProgress.column.name}
      >
        <Text
          format="rubik-500"
          className="text-accent"
          text={dndOrdenationColumnProgress.column.name}
        />
      </ConditionalRenderer>

      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable
          droppableId="dndOrdenationItemProgressList"
          isDropDisabled={isDisabled}
        >
          {provided => (
            <div
              className="flex flex-col w-full gap-3"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {itemsState.map((dndOrdenationItemProgress, index) => {
                if (columnProgress) {
                  const findDndItemProgress = columnProgress.itemsProgress.find(
                    itemProgress =>
                      itemProgress.id === dndOrdenationItemProgress.id,
                  );

                  if (findDndItemProgress) {
                    const findAnswer = answerColumn?.items.find(
                      ({ id }) => id === findDndItemProgress.item.id,
                    );

                    return (
                      <DNDOrdenationItemProgress
                        index={index}
                        key={dndOrdenationItemProgress.id}
                        dndOrdenationItemProgress={findDndItemProgress}
                        activityMode={activityMode}
                        isDisabled={isDisabled}
                        answerOrder={findAnswer?.order}
                      />
                    );
                  }
                }

                return (
                  <DNDOrdenationItemProgress
                    index={index}
                    key={dndOrdenationItemProgress.id}
                    dndOrdenationItemProgress={dndOrdenationItemProgress}
                    activityMode={activityMode}
                    isDisabled={isDisabled}
                  />
                );
              })}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
